import { useMemo } from 'react'

import useOfferList from 'hooks/Offers/useOfferList'
import { MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { OFFER_TYPE_HOTEL } from 'constants/offer'
import Places from 'constants/places'
import config from 'constants/config'
import { FlightsClassTypes } from 'constants/flight'
import { useMaldivesDirectHotelStreamSearch } from 'components/MaldivesDirect/MaldivesDirectSearchPage/hooks/useMaldivesDirectHotelStreamSearch'

interface Props {
  excludedIds?: Array<string>
  /** When false, skip the streamed bundle-pricing fetch (e.g. rewards landing page with reward preview pricing). */
  includeBundleStream?: boolean
}

interface MaldivesCharterOfferList extends App.OfferList {
  bundleFilters?: App.OfferListFilters
}

/**
 * Fetches Maldives charter flight bundle offers with pricing
 * Uses the campaign tag to filter offers that are part of the Maldives Direct program
 * Also fetches bundle pricing (hotel + flight) for the next available charter period
 */
export default function useMaldivesCharterRecommendedOfferList({
  excludedIds,
  includeBundleStream = true,
}: Props): MaldivesCharterOfferList {
  const defaultRooms = useMemo(() => [config.search.defaultOccupants], [])

  // Filters for fetching offer list (without dates, for general listing)
  const listFilters = useMemo<App.OfferListFilters>(() => ({
    offerTypes: [OFFER_TYPE_HOTEL],
    destinationId: Places.Maldives.id,
    campaigns: [MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG],
    frontPageVisibleOnly: true,
    limit: 25,
  }), [])

  const { bundleFilters, canStreamHotelOffers } = useMaldivesDirectHotelStreamSearch({
    checkInDate: undefined,
    checkOutDate: undefined,
    rooms: defaultRooms,
    fareClassForSearch: FlightsClassTypes.ECONOMY,
    holidayTypes: undefined,
    amenities: undefined,
    sortBy: undefined,
    isTourBundleMode: false,
  })

  const offerList = useOfferList(listFilters, { excludedIds, preferListingOffers: true })

  // Also fetch with bundle filters for pricing (this triggers bundle price calculation)
  useOfferList(bundleFilters, {
    disabled: !canStreamHotelOffers || !includeBundleStream,
    minRequiredOffers: 1,
    preferListingOffers: true,
  })

  return {
    ...offerList,
    bundleFilters,
  }
}
